import type { HTMLAttributes, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import ClipLoader from 'react-spinners/ClipLoader';

import { faXmark } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    Dialog as HeadlessUiDialog,
    DialogPanel as HeadlessUiDialogPanel,
    DialogTitle as HeadlessUiDialogTitle,
    Transition,
    TransitionChild,
} from '@headlessui/react';
import type { ClassValue } from 'clsx';
import { clsx } from 'clsx';

import { useSideBarContext } from '@spacefill/shared/src/utils/contexts/SideBarContext';

import { cn } from '../../lib/utils';
import type { ButtonProps, ButtonTypes } from '../Button';
import { Button } from '../Button';
import type { ButtonSubmitTypes } from '../Button/Button';

type ModalProps = {
    isOpen: boolean;
    title: ReactNode;
    children?: ReactNode;
    onClose: () => void;
    onConfirm?: () => void;
    onAfterClose?: () => void;
    confirmLabel?: string;
    closeLabel?: string;
    dataTestid?: string;
    htmlAttributes?: HTMLAttributes<HTMLDivElement>;
    confirmButtonProps?: ButtonTypes | ButtonSubmitTypes;
    size?: 'sm' | 'md' | 'lg' | 'xl' | '2xl' | '3xl' | '4xl';
    isProcessing?: boolean;
    processingLabel?: string;
    hideCancel?: boolean;
    hideConfirm?: boolean;
    additionalButton?: ReactNode;
    footer?: ReactNode;
    classNameContent?: ClassValue;
    showCloseIcon?: boolean;
};

export const Modal = ({
    isOpen,
    title,
    children,
    onClose,
    onConfirm,
    onAfterClose,
    closeLabel,
    confirmLabel,
    dataTestid,
    htmlAttributes,
    confirmButtonProps = {},
    size = 'md',
    isProcessing = false,
    processingLabel,
    hideCancel = false,
    hideConfirm = false,
    additionalButton,
    footer = undefined,
    classNameContent,
    showCloseIcon = false,
}: ModalProps) => {
    const { t } = useTranslation();
    const { isSizeBarCollapsed } = useSideBarContext();

    const { type, disabled, ...restProps } = confirmButtonProps;
    const confirmButtonPropsWithDefaults: ButtonProps = {
        type: type || 'button',
        dataTestid: 'confirm-button',
        onClick: onConfirm || (() => {}),
        disabled: disabled! || isProcessing,
        ...restProps,
    };

    return (
        <Transition
            show={isOpen}
            appear={true}
        >
            <HeadlessUiDialog
                onClose={isProcessing ? () => {} : onClose}
                className='relative'
                data-testid={dataTestid}
            >
                <TransitionChild
                    enter='duration-300 ease-out'
                    enterFrom='opacity-0'
                    enterTo='opacity-100'
                    leave='duration-300 ease-in'
                    leaveFrom='opacity-100'
                    leaveTo='opacity-0'
                    afterLeave={onAfterClose}
                >
                    <div
                        className='fixed inset-0 z-modal bg-black/70'
                        aria-hidden='true'
                    />
                </TransitionChild>
                <div
                    className={clsx(
                        'fixed inset-0 z-modal flex items-center justify-center p-4',
                        isSizeBarCollapsed ? 'left-sidebar-collapsed' : 'left-sidebar-expanded'
                    )}
                    data-component-name='Modal'
                    {...htmlAttributes}
                >
                    <TransitionChild
                        enter='transition duration-300 ease-out'
                        enterFrom='transform -translate-y-4 opacity-0'
                        enterTo='transform translate-y-0 opacity-100'
                        leave='transition duration-300 ease-in'
                        leaveFrom='transform translate-y-0 opacity-100'
                        leaveTo='transform -translate-y-4 opacity-0'
                    >
                        <HeadlessUiDialogPanel
                            className={clsx('my-6 flex max-h-full flex-col rounded-xl bg-white p-8 drop-shadow-md', {
                                'w-96': size === 'sm',
                                'w-lg': size === 'md',
                                'w-160': size === 'lg',
                                'w-184': size === 'xl',
                                'w-208': size === '2xl',
                                'w-248': size === '3xl',
                                'max-w-7xl w-full': size === '4xl',
                            })}
                        >
                            {showCloseIcon ? (
                                <div className='mb-2 flex items-start justify-between gap-4'>
                                    <HeadlessUiDialogTitle className='min-w-0 flex-1 text-lg font-bold'>
                                        {title}
                                    </HeadlessUiDialogTitle>
                                    <button
                                        type='button'
                                        onClick={onClose}
                                        aria-label={t('Close')}
                                        data-testid='modal-close-icon'
                                        className='shrink-0 cursor-pointer text-gray-500 hover:text-gray-700'
                                    >
                                        <FontAwesomeIcon icon={faXmark} />
                                    </button>
                                </div>
                            ) : (
                                <HeadlessUiDialogTitle className='mb-2 text-lg font-bold'>
                                    {title}
                                </HeadlessUiDialogTitle>
                            )}
                            <div className={cn(`mb-10 grow overflow-auto leading-5`, classNameContent)}>{children}</div>
                            {footer ? (
                                <>{footer}</>
                            ) : footer === undefined ? (
                                <div className='flex justify-between'>
                                    {!hideCancel && (
                                        <Button
                                            dataTestid='cancel-button'
                                            variant='outlined'
                                            onClick={onClose}
                                            disabled={isProcessing}
                                        >
                                            {closeLabel || t('Close')}
                                        </Button>
                                    )}
                                    <div className='flex gap-2'>{additionalButton}</div>
                                    {(onConfirm || confirmButtonProps) && !hideConfirm && (
                                        <Button {...confirmButtonPropsWithDefaults}>
                                            {isProcessing ? (
                                                <>
                                                    <ClipLoader
                                                        loading={true}
                                                        className='opacity-50'
                                                        size={20}
                                                    />
                                                    {processingLabel || t('Processing')}
                                                </>
                                            ) : (
                                                confirmLabel || t('Confirm')
                                            )}
                                        </Button>
                                    )}
                                </div>
                            ) : null}
                        </HeadlessUiDialogPanel>
                    </TransitionChild>
                </div>
            </HeadlessUiDialog>
        </Transition>
    );
};
